<template>
    <div class="fx-date-range">
        <fx-select
            v-if="presets && presets.length"
            :value="selectedRangeOption"
            :options="dateOptions"
            :default-option="{ label: defaultOptionCopy, value: null }"
            :align="align"
            :disable-native="true"
            @input="onOptionSelected"
        >
            <template v-show="!value" slot="caret">
                <slot name="caret">
                    <span class="icon-caret-down"></span>
                </slot>
            </template>
        </fx-select>
        <div
            v-else
            class="fx-custom-date-range-select"
            tabindex="0"
            @keydown.enter="setCustomRangeOpen(!isCustomRangeOpen)"
            @click="setCustomRangeOpen(!isCustomRangeOpen)"
        >
            <span>
                {{
                    selectedRangeOption === 'custom-range'
                        ? formattedDateRange || selectCustomRangeCopy
                        : defaultOptionCopy
                }}
            </span>
            <span v-if="value" class="icon-cancel" tabindex="0" @keydown.enter.stop="reset" @click.stop="reset"></span>
            <slot name="caret" v-else>
                <span class="icon-caret-down"></span>
            </slot>
        </div>
        <div
            v-if="isCustomRangeOpen"
            class="fx-date-picker--desktop"
            :class="{
                'align-left': align === 'left',
                'align-right': align === 'right'
            }"
        >
            <fx-date
                :max-date="maxDate"
                :min-date="minDate"
                v-on-click-outside.static="() => setCustomRangeOpen(false)"
                :value="null"
                @input="onCustomRangeSelected"
                mode="range"
            ></fx-date>
        </div>
    </div>
</template>

<script>
import { mixin as onClickOutside } from 'vue-on-click-outside'
import SelectComponent from './SelectComponent'
import DateComponent from './DateComponent'
import DateUtil from '../utilities/DateUtil'

export default {
    props: {
        value: {
            required: true
        },
        presets: {
            type: Array,
            default: null
            /*
                [{
                    key: 'tomorrow',
                    label: 'Tomorrow',
                    from: Date,
                    to: Date
                }]
            */
        },
        minDate: [Date, String],
        maxDate: [Date, String],
        dateFormat: {
            type: String,
            default: 'MMM D'
        },
        align: { type: String, default: 'left' },
        defaultOptionCopy: { type: String, default: 'All Dates' },
        customOptionCopy: { type: String, default: 'Custom' },
        selectCustomRangeCopy: { type: String, default: 'Select a date range' }
    },
    data () {
        return {
            selectedRangeOption: null,
            isCustomRangeOpen: false,
            previousSelectedRangeOption: null
        }
    },
    computed: {
        dateOptions () {
            let options = null
            if (this.presets && this.presets.length) {
                options = this.presets.map((preset) => ({
                    value: preset.key,
                    label: preset.label
                }))
                options.push({
                    value: 'new-range',
                    label: this.selectedRangeOption === 'new-range' ? this.selectCustomRangeCopy : this.customOptionCopy
                })
                if (this.selectedRangeOption === 'custom-range') {
                    options.push({
                        value: 'custom-range',
                        label: this.formattedDateRange
                    })
                }
            }
            return options
        },
        formattedDateRange () {
            let dateRange = null
            if (this.value) {
                const dateStart = this.$formatDate(this.value[0], this.dateFormat)
                const dateEnd = this.$formatDate(this.value[1], this.dateFormat)
                dateRange = `${dateStart} - ${dateEnd}`
            }
            return dateRange
        }
    },
    watch: {
        value (newRange) {
            this.setSelectionByRange(newRange)
        }
    },
    created () {
        this.setSelectionByRange(this.value)
    },
    methods: {
        setSelection (selection) {
            if (selection !== this.selectedRangeOption) {
                this.previousSelectedRangeOption = this.selectedRangeOption
                this.selectedRangeOption = selection
            }
        },
        setCustomRangeOpen (open) {
            this.isCustomRangeOpen = open
            if (open) {
                this.setSelection('custom-range')
            } else {
                this.selectedRangeOption = this.previousSelectedRangeOption
            }
        },
        setSelectionByRange (range) {
            if (range) {
                let selectedPreset = null
                if (this.presets && this.presets.length) {
                    selectedPreset = this.presets.find(
                        (preset) =>
                            DateUtil.areDatesEqual(range[0], preset.from) && DateUtil.areDatesEqual(range[1], preset.to)
                    )
                }
                this.setSelection(selectedPreset ? selectedPreset.key : 'custom-range')
            } else {
                this.setSelection(null)
            }
        },
        reset () {
            this.isCustomRangeOpen = false
            this.setSelection(null)
            this.$emit('input', null)
        },
        onOptionSelected (selection) {
            this.isCustomRangeOpen = false
            if (!selection) {
                this.$emit('input', null)
            } else if (selection === 'new-range') {
                this.isCustomRangeOpen = true
            } else {
                const preset = this.presets.find((range) => range.key === selection)
                this.$emit('input', [preset.from, preset.to])
            }
            this.setSelection(selection)
        },
        onCustomRangeSelected (range) {
            this.isCustomRangeOpen = false
            this.$emit('input', range)
        }
    },
    components: {
        'fx-select': SelectComponent,
        'fx-date': DateComponent
    },
    mixins: [onClickOutside]
}
</script>

<style lang="postcss">
@import 'theme';

.fx-date-range {
    position: static;
    @media (--mq-large-min) {
        position: relative;
    }
    & .flatpickr-calendar {
        border: none !important;
        & > .inline {
            top: 0 !important;
        }
    }
}
</style>

<style lang="postcss" scoped>
@import 'theme';

.fx-date-picker--desktop {
    -webkit-transform: translate3d(0,0,0);
    background-color: white;
    border-radius: var(--d-border-radius);
    box-shadow: var(--sh-shallow);
    overflow: hidden;
    position: absolute;
    z-index: var(--layer-1);

    &.align-left {
        left: 50vw;
        margin-left: -150px;
    }
    &.align-right {
        margin-right: calc(-150px - var(--site-padding));
        right: 50vw;
    }
    @media (--mq-large-min) {
        &.align-left {
            left: 0;
            margin-left: 0;
        }
        &.align-right {
            right: 0;
            margin-right: 0;
        }
    }

}

.fx-custom-date-range-select {
    display: inline-flex;
    align-items: center;
    padding: 4px 0;
    cursor: pointer;
    border-bottom: solid 2px transparent;
    transition: border-color 100ms ease-in-out;
    outline: none;
    &:focus {
        border-color: var(--c-gray-1);
    }
    & > .icon-caret-down,
    & > .icon-cancel {
        margin-left: 4px;
        font-size: var(--fs-small);
    }
}
</style>
